export type PeriodType = 'week' | 'month'

export interface DashboardViewConfig {
  vault_ids?: (number | string)[] | null
  split_by_vault?: boolean
  horizon?: number
  period?: PeriodType
}

export interface SavedViewRecord {
  id: number
  name: string
  is_default: boolean
  config: DashboardViewConfig
}

export interface TeamDefaults {
  period_type: string // raw from the API, e.g. "Week" / "Month"
  default_horizon: number
}

export interface ResolvedDashboardSettings {
  vaultIds: (number | string)[] | null
  splitByVault: boolean
  horizon: number
  period: PeriodType
}

/** The one saved view (if any) marked as default, for this surface. */
export function findDefaultView(views: SavedViewRecord[]): SavedViewRecord | null {
  return views.find((v) => v.is_default) ?? null
}

/**
 * Load precedence for the dashboard: the user's default view for
 * surface='dashboard', otherwise the team's period_type/default_horizon.
 * Selecting or applying a view never writes back to team settings — this
 * only ever reads them as a fallback.
 */
export function resolveInitialDashboardSettings(
  views: SavedViewRecord[],
  teamDefaults: TeamDefaults,
): ResolvedDashboardSettings {
  const fallbackPeriod = (teamDefaults.period_type ?? 'week').toLowerCase() as PeriodType
  const defaultView = findDefaultView(views)

  if (!defaultView) {
    return {
      vaultIds: null,
      splitByVault: false,
      horizon: teamDefaults.default_horizon,
      period: fallbackPeriod,
    }
  }

  const config = defaultView.config ?? {}
  return {
    vaultIds: config.vault_ids ?? null,
    splitByVault: config.split_by_vault ?? false,
    horizon: config.horizon ?? teamDefaults.default_horizon,
    period: config.period ?? fallbackPeriod,
  }
}
